import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import { formatDateTime } from '../utils/dateFormat';
const pad = (n) => String(n).padStart(2, '0');

// ISO instant -> value for <input type="datetime-local"> in the admin's local time.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// datetime-local value (admin's local time) -> ISO instant.
const toIso = (local) => (local ? new Date(local).toISOString() : null);

const formatWhen = (iso) => (iso ? formatDateTime(iso) : '—');

const emptyForm = () => ({
  message: '',
  severity: 'INFO',
  startsAt: toLocalInput(new Date().toISOString()),
  endsAt: toLocalInput(new Date(Date.now() + 24 * 3600 * 1000).toISOString()),
  eventAt: '',
  isActive: true
});

function statusOf(a) {
  const now = Date.now();
  if (!a.isActive) return { label: 'Inactive', variant: 'secondary' };
  if (Date.parse(a.endsAt) < now) return { label: 'Ended', variant: 'secondary' };
  if (Date.parse(a.startsAt) > now) return { label: 'Scheduled', variant: 'info' };
  return { label: 'Live', variant: 'success' };
}

/** System Admin management of the platform-wide announcement strip (see AnnouncementStrip.js). */
function AnnouncementsAdmin() {
  const [announcements, setAnnouncements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  // Once the admin edits "Hide after" themselves, stop auto-filling it from the maintenance time.
  const endsAtTouched = useRef(false);

  const load = useCallback(async () => {
    try {
      const response = await axios.get('/api/system-admin/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Error fetching announcements');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    endsAtTouched.current = false;
    setShowModal(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    endsAtTouched.current = true;
    setForm({
      message: a.message,
      severity: a.severity,
      startsAt: toLocalInput(a.startsAt),
      endsAt: toLocalInput(a.endsAt),
      eventAt: toLocalInput(a.eventAt),
      isActive: a.isActive
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      message: form.message,
      severity: form.severity,
      startsAt: toIso(form.startsAt),
      endsAt: toIso(form.endsAt),
      eventAt: toIso(form.eventAt),
      isActive: form.isActive
    };
    try {
      if (editing) {
        await axios.put(`/api/system-admin/announcements/${editing.id}`, payload);
        toast.success('Announcement updated');
      } else {
        await axios.post('/api/system-admin/announcements', payload);
        toast.success('Announcement created');
      }
      setShowModal(false);
      load();
    } catch (error) {
      const msg = error.response?.data?.error || 'Error saving announcement';
      toast.error(typeof msg === 'string' ? msg : 'Error saving announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`/api/system-admin/announcements/${a.id}`);
      toast.success('Announcement deleted');
      load();
    } catch (error) {
      toast.error('Error deleting announcement');
    }
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Announcements</h5>
        <Button size="sm" onClick={openCreate}>
          <i className="bi bi-plus-lg me-1" />
          New announcement
        </Button>
      </Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Shown as a thin strip across the app and the login page between the start and end times.
          Set a maintenance time to make the strip turn amber and count down in the last 15 minutes.
        </p>
        <Table responsive hover size="sm">
          <thead>
            <tr>
              <th>Message</th>
              <th>Type</th>
              <th>Visible</th>
              <th>Maintenance time</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {announcements.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted">No announcements yet</td>
              </tr>
            )}
            {announcements.map((a) => {
              const status = statusOf(a);
              return (
                <tr key={a.id}>
                  <td>{a.message}</td>
                  <td>{a.severity === 'WARNING' ? 'Warning' : 'Info'}</td>
                  <td className="small">{formatWhen(a.startsAt)} → {formatWhen(a.endsAt)}</td>
                  <td className="small">{formatWhen(a.eventAt)}</td>
                  <td><Badge bg={status.variant}>{status.label}</Badge></td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(a)}>Edit</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(a)}>Delete</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title>{editing ? 'Edit announcement' : 'New announcement'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Message *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                maxLength={500}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Scheduled maintenance tonight 11:00 PM (about 2 min). Please save your work."
                required
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <Form.Select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                    <option value="INFO">Info</option>
                    <option value="WARNING">Warning</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Maintenance time (optional)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={form.eventAt}
                    onChange={(e) => {
                      const eventAt = e.target.value;
                      const next = { ...form, eventAt };
                      if (eventAt && !endsAtTouched.current) {
                        next.endsAt = toLocalInput(new Date(new Date(eventAt).getTime() + 3600 * 1000).toISOString());
                      }
                      setForm(next);
                    }}
                  />
                  <Form.Text className="text-muted">
                    Hide time defaults to one hour after this unless you change it.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Show from *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={form.startsAt}
                    onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Hide after *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={form.endsAt}
                    onChange={(e) => {
                      endsAtTouched.current = true;
                      setForm({ ...form, endsAt: e.target.value });
                    }}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Check
              type="switch"
              id="announcement-active"
              label="Active"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Card>
  );
}

export default AnnouncementsAdmin;
